import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  attemptDetailsContent: {
    width: '100%',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  statBox: {
    flex: '0 0 auto',
    minWidth: 70,
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
  },
  statBoxTotal: {
    backgroundColor: theme.palette.partnerBlue.light,
  },
  statBoxCorrect: {
    backgroundColor: theme.palette.greenSnowFlurry,
  },
  statBoxWrong: {
    backgroundColor: theme.palette.statusLabel.error.secondary,
  },
  statBoxPartial: {
    backgroundColor: theme.palette.warning.light,
  },
  statBoxUnanswered: {
    backgroundColor: theme.palette.action.hover,
  },
  statLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },

  /* ── Question card ── */
  questionCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  questionCardCorrect: {
    borderColor: theme.palette.statusLabel.success.primary,
  },
  questionCardPartial: {
    borderColor: theme.palette.warning.main,
  },
  questionCardWrong: {
    borderColor: theme.palette.statusLabel.error.primary,
  },
  questionCardUnanswered: {
    borderColor: theme.palette.divider,
  },

  /* Card header */
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  cardHeaderCorrect: {
    borderBottomColor: theme.palette.statusLabel.success.primary,
  },
  cardHeaderPartial: {
    borderBottomColor: theme.palette.warning.main,
  },
  cardHeaderWrong: {
    borderBottomColor: theme.palette.statusLabel.error.primary,
  },
  cardHeaderUnanswered: {
    borderBottomColor: theme.palette.divider,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: 600,
  },
  statusLabelCorrect: {
    color: theme.palette.statusLabel.success.primary,
  },
  statusLabelPartial: {
    color: theme.palette.warning.dark,
  },
  statusLabelWrong: {
    color: theme.palette.statusLabel.error.primary,
  },
  statusLabelUnanswered: {
    color: theme.palette.text.secondary,
  },
  iconCorrect: {
    color: theme.palette.statusLabel.success.primary,
    fontSize: 20,
  },
  iconPartial: {
    color: theme.palette.warning.main,
    fontSize: 20,
  },
  iconWrong: {
    color: theme.palette.statusLabel.error.primary,
    fontSize: 20,
  },
  iconUnanswered: {
    color: theme.palette.text.disabled,
    fontSize: 20,
  },

  /* Two-column body */
  cardBody: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  leftColumn: {
    flex: '0 0 42%',
    padding: theme.spacing(2, 2.5),
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  questionStatement: {
    fontSize: 14,
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  explanationBox: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid`,
  },
  explanationBoxCorrect: {
    backgroundColor: theme.palette.greenSnowFlurry,
    borderColor: theme.palette.statusLabel.success.primary,
  },
  explanationBoxPartial: {
    backgroundColor: theme.palette.warning.light,
    borderColor: theme.palette.warning.main,
  },
  explanationBoxWrong: {
    backgroundColor: theme.palette.statusLabel.error.secondary,
    borderColor: theme.palette.statusLabel.error.primary,
  },
  explanationBoxUnanswered: {
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.divider,
  },
  explanationTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  explanationTitleCorrect: {
    color: theme.palette.statusLabel.success.primary,
  },
  explanationTitlePartial: {
    color: theme.palette.warning.dark,
  },
  explanationTitleWrong: {
    color: theme.palette.statusLabel.error.primary,
  },
  explanationTitleUnanswered: {
    color: theme.palette.text.secondary,
  },
  explanationText: {
    fontSize: 13,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  /* Right column — alternatives */
  rightColumn: {
    flex: 1,
    padding: theme.spacing(2, 2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  alternativeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  alternativeRowCorrect: {
    borderColor: theme.palette.statusLabel.success.primary,
    backgroundColor: theme.palette.greenSnowFlurry,
  },
  alternativeRowWrong: {
    borderColor: theme.palette.statusLabel.error.primary,
    backgroundColor: theme.palette.statusLabel.error.secondary,
  },
  alternativeLetter: {
    width: 26,
    height: 26,
    minWidth: 26,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  alternativeLetterCorrect: {
    borderColor: theme.palette.statusLabel.success.primary,
    backgroundColor: theme.palette.statusLabel.success.primary,
    color: theme.palette.white,
  },
  alternativeLetterWrong: {
    borderColor: theme.palette.statusLabel.error.primary,
    backgroundColor: theme.palette.statusLabel.error.primary,
    color: theme.palette.white,
  },
  alternativeContent: {
    fontSize: 13,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    paddingTop: 3,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  disabledReviewAlert: {
    marginBottom: theme.spacing(3),
  },
  summaryLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  attemptCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: `3px solid ${theme.palette.common.black}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  attemptNumber: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.common.black,
  },
  attemptOf: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  summaryScore: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}))

export default useStyles
